import {
  Link as TanStackLink,
  Outlet,
  useLocation as useTanStackLocation,
  useNavigate as useTanStackNavigate,
  useParams as useTanStackParams,
  useRouter,
} from '@tanstack/react-router';
import { ComponentProps, FC, useCallback, useMemo } from 'react';

type PathParams = Record<string, string | number | null | undefined>;
type NavigateOptions = {
  replace?: boolean;
  state?: Record<string, unknown>;
};
type SetSearchParams = (
  next:
    | URLSearchParams
    | string
    | Record<string, string>
    | ((current: URLSearchParams) => URLSearchParams),
  options?: Pick<NavigateOptions, 'replace'>,
) => void;

export const generatePath = (pattern: string, params: PathParams = {}) =>
  pattern.replace(/:([A-Za-z0-9_]+)\??/g, (match, key: string) => {
    const value = params[key];

    if (value === undefined || value === null) {
      return match.endsWith('?') ? '' : match;
    }

    return String(value);
  });

export const useNavigate = () => {
  const navigate = useTanStackNavigate();
  const router = useRouter();

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        return router.history.go(to);
      }

      return navigate({
        to,
        replace: options?.replace,
        state: options?.state,
      });
    },
    [navigate, router],
  );
};

export const useParams = <T extends Record<string, string | undefined>>() =>
  (useTanStackParams({ strict: false, shouldThrow: false } as never) ?? {}) as T;

export const useLocation = () => {
  const location = useTanStackLocation();

  return {
    ...location,
    search: location.searchStr,
  };
};

const toSearchParams = (
  next: URLSearchParams | string | Record<string, string>,
): URLSearchParams => {
  if (next instanceof URLSearchParams || typeof next === 'string') {
    return new URLSearchParams(next);
  }

  return new URLSearchParams(next);
};

export const useSearchParams = (): [URLSearchParams, SetSearchParams] => {
  const location = useTanStackLocation();
  const navigate = useTanStackNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.searchStr), [location.searchStr]);

  const setSearchParams = useCallback<SetSearchParams>(
    (next, options) => {
      const current = new URLSearchParams(location.searchStr);
      const resolved = typeof next === 'function' ? next(current) : toSearchParams(next);
      const search = resolved.toString();

      void navigate({
        to: `${location.pathname}${search ? `?${search}` : ''}${location.hash}`,
        replace: options?.replace,
      });
    },
    [location.hash, location.pathname, location.searchStr, navigate],
  );

  return [searchParams, setSearchParams];
};

type LinkProps = Omit<ComponentProps<'a'>, 'href'> & {
  to: string;
  replace?: boolean;
};

export const Link = TanStackLink as unknown as FC<LinkProps>;

export { Outlet };
